import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useSWRConfig } from 'swr';
import { getApprovalEditUrl } from '@/utils/urls';
import { APPROVAL_V2_URL } from '@/constant/approval';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import useDeleteApproval, {
  getDeleteApprovalErrorMessage,
} from '@/hooks/approval/useDeleteApproval';
import useApprovalView from '@/hooks/approval/useApprovalView';
import useUpdateApproval from '@/hooks/approval/useUpdateApproval';
import type { CreateApprovalRequest } from '@/types/approval';
import { parseContractIds } from '@/utils/approval/contractIds';
import { parseSaVoucherAttributes } from '@/utils/approval/saVoucher';
import {
  buildCancelAttributes,
  isCancelApprovalType,
  parseCancelTargetSnapshot,
} from '@/utils/approval/cancelApproval';
import {
  isFncoPreMarketingType,
  pickFncoPreMarketingRequestAttributes,
} from '@/utils/approval/fncoPreMarketing';
import {
  isMarketingExpenseType,
  pickMarketingExpenseRequestAttributes,
} from '@/utils/approval/marketingExpense';
import {
  isGroupSalesMultiType,
  parseGroupSalesMultiAttributes,
} from '@/utils/approval/groupSalesMulti';
import {
  isExpenseVoucherType,
  parseExpenseVoucherAttributes,
  pickExpenseVoucherRequestAttributes,
} from '@/utils/approval/expenseVoucher';
import {
  findSubmitViolation,
  type SubmitCheckContext,
} from '@/utils/approval/submitChecks';
import type { ApprovalViewData } from './types';

/**
 * 임시저장 문서 상신 페이로드. 전용 상신 엔드포인트가 없어 편집 저장과 같은 PUT 에 status=SUBMIT.
 * PUT 이 전량 교체라(요청에 없는 첨부 fileId 는 서버가 삭제) 저장값 전체를 되실어야 함.
 *
 * 작성화면 approvalFormToCreatePayload 로 대체 금지 — 결재 순번을 insertIndex 기준으로 재계산해
 * 서버가 준 order 가 바뀌고, 본문 font-family 를 strip 해 편집 없는 상신에서 본문이 변경됨.
 */
const toSubmitPayload = (
  data: ApprovalViewData,
  opinion: string,
): CreateApprovalRequest => {
  const line = data.approvers ?? [];
  const pick = (type: 1 | 2) =>
    line
      .filter((a) => a.type === type)
      .map((a) => ({ passportId: a.passportId, order: a.order }));
  const coApprovers = pick(2);
  // 저장 스키마엔 추가참조인 필드가 없어 한 배열로 합침 (편집 저장과 동일)
  const referrers = [
    ...(data.referrers ?? []),
    ...(data.additionalReferrers ?? []),
  ].map((r) => ({ passportId: r.passportId }));
  const attachments = (data.attachments ?? [])
    .filter((a) => !a.isDeleted)
    .map((a) => ({ fileId: a.fileId }));

  return {
    type: data.type.code,
    subject: data.subject,
    content: data.content,
    status: 'SUBMIT',
    opinion: opinion.trim().length > 0 ? opinion : null,
    // 조회 타입에만 남아 있는 레거시 3(사업부공개) 때문에 저장 타입과 안 겹침. 값 유실 대신 통과
    securityLevel: (data.security ??
      null) as CreateApprovalRequest['securityLevel'],
    approvers: pick(1),
    coApprovers: coApprovers.length > 0 ? coApprovers : null,
    referrers: referrers.length > 0 ? referrers : null,
    // 취소 품의 subject/submittedAt/url, 221·212 data/internalOrderNumber, 111 합계는 서버 소유 — 되실으면 422
    attributes: isCancelApprovalType(data.type.code)
      ? buildCancelAttributes(parseCancelTargetSnapshot(data.attributes))
      : isFncoPreMarketingType(data.type.code)
      ? pickFncoPreMarketingRequestAttributes(data.attributes)
      : isMarketingExpenseType(data.type.code)
      ? pickMarketingExpenseRequestAttributes(data.attributes)
      : isExpenseVoucherType(data.type.code)
      ? pickExpenseVoucherRequestAttributes(data.attributes)
      : data.attributes ?? null,
    attachments: attachments.length > 0 ? attachments : null,
    showImagesBelowContent: data.showImagesBelowContent,
  };
};

/**
 * 상신 검사 입력. 조회 approvers 엔 기안자 자동승인 행이 섞일 수 있어 기안자 제외.
 * 소속은 조회 응답의 결재자 코드로 판정
 */
const toSubmitCheckContext = (data: ApprovalViewData): SubmitCheckContext => {
  const members = (data.approvers ?? []).filter(
    (a) => a.passportId !== data.author.passportId,
  );
  return {
    apvType: data.type.code,
    subject: data.subject ?? '',
    lineMemberIds: members.map((a) => a.passportId),
    contractIds: parseContractIds(data.attributes?.contractIds),
    cancelTarget: parseCancelTargetSnapshot(data.attributes),
    sapDocumentNo:
      parseSaVoucherAttributes(data.attributes)?.sapDocumentNo ?? null,
    coApproverIds: members.filter((a) => a.type === 2).map((a) => a.passportId),
    memberOrganizations: new Map(
      members.map((a) => [
        a.passportId,
        a.divisionCode && a.departmentCode
          ? {
              divisionCode: a.divisionCode,
              departmentCode: a.departmentCode,
              intranetId: a.intranetId,
            }
          : null,
      ]),
    ),
    groupSalesMulti: isGroupSalesMultiType(data.type.code)
      ? parseGroupSalesMultiAttributes(data.attributes)
      : undefined,
    expenseVoucher: isExpenseVoucherType(data.type.code)
      ? parseExpenseVoucherAttributes(data.attributes)
      : undefined,
  };
};

/** 상신 실패 시 status code → 사용자 메시지(번역 key) 매핑. */
export const getSubmitApprovalErrorMessage = (error: unknown): string => {
  if (!axios.isAxiosError(error)) return '품의 상신에 실패했습니다.';
  const serverMessage = error.response?.data?.message as string | undefined;
  switch (error.response?.status) {
    case 403:
      return '본인이 작성한 문서만 상신할 수 있습니다.';
    case 404:
      return '문서를 찾을 수 없습니다.';
    case 422:
      return serverMessage || '상신 가능한 상태가 아닙니다.';
    default:
      return serverMessage || '품의 상신에 실패했습니다.';
  }
};

/**
 * 편집 / 문서삭제 / 상신 동작 — 액션 버튼 row(DocumentActions)와 더보기 메뉴(ActionMenu)에서 공유.
 * 레거시도 같은 항목을 두 위치에 노출하므로 동작 분기 없이 하나로 유지.
 * 노출 조건 판정은 호출부(computeActionVisibility) 소관.
 */
type UseDocumentActionHandlersOptions = {
  documentId: string;
  /** 호스트(모달)가 in-place 편집 전환을 처리하려는 경우. 미제공 시 router.push 로 페이지 이동. */
  onEdit?: () => void;
  /** 삭제 성공 후 호스트 후처리 (모달 닫기 / 목록 이동 + 리스트 동기화). 미제공 시 no-op. */
  onDeleted?: () => void;
  /** 상신 성공 후 호스트 후처리 (외부 목록을 진행중으로 동기화). 미제공 시 no-op. */
  onSubmitted?: () => void;
};

const useDocumentActionHandlers = ({
  documentId,
  onEdit,
  onDeleted,
  onSubmitted,
}: UseDocumentActionHandlersOptions) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dialog = useDialog();
  const { showSuccess, showError } = useSnackbar();
  const { remove } = useDeleteApproval();
  // 조회화면과 같은 SWR key — 추가 요청 없이 캐시 재사용
  const { data: detail } = useApprovalView(documentId);
  const { mutate } = useSWRConfig();
  const { update, isSubmitting } = useUpdateApproval();

  const handleEdit = useCallback(() => {
    if (onEdit) {
      onEdit();
      return;
    }
    router.push(getApprovalEditUrl(documentId));
  }, [onEdit, router, documentId]);

  const handleDelete = useCallback(() => {
    dialog.show({
      message: t('이 문서를 삭제하시겠습니까?'),
      cancelButtonText: t('취소'),
      confirmButtonText: t('삭제'),
      // 되돌릴 수 없는 확인이라 어느 호스트(모달/다이얼로그) 안에서든 최상위 유지
      forceToFront: true,
      onConfirm: async () => {
        try {
          await remove(documentId);
          showSuccess({ message: t('품의 삭제가 완료되었습니다.') });
          onDeleted?.();
        } catch (error) {
          showError({ message: t(getDeleteApprovalErrorMessage(error)) });
        }
      },
    });
  }, [dialog, t, remove, documentId, showSuccess, showError, onDeleted]);

  /**
   * 상신 — 의견입력 모달의 [결재] 가 호출. 성공 여부를 반환해 호출부가 모달 닫기를 결정.
   */
  const handleSubmit = useCallback(
    async (opinion: string): Promise<boolean> => {
      if (!detail || isSubmitting) return false;
      const violation = findSubmitViolation(toSubmitCheckContext(detail));
      if (violation) {
        showError({ message: t(violation) });
        return false;
      }
      try {
        await update(documentId, toSubmitPayload(detail, opinion));
        // 문자열 key(useApproval) + 튜플 key(useApprovalView) 두 shape 동시 매칭 필요.
        // 재검증 실패는 무시 — 상신 성공 판정을 뒤집으면 안 됨
        const baseKey = `${APPROVAL_V2_URL}/${documentId}`;
        await mutate(
          (cacheKey) =>
            cacheKey === baseKey ||
            (Array.isArray(cacheKey) && cacheKey[0] === baseKey),
        ).catch(() => undefined);
        onSubmitted?.();
        showSuccess({ message: t('품의 상신이 완료되었습니다.') });
        return true;
      } catch (error) {
        showError({ message: t(getSubmitApprovalErrorMessage(error)) });
        return false;
      }
    },
    [
      detail,
      isSubmitting,
      update,
      documentId,
      mutate,
      onSubmitted,
      showSuccess,
      showError,
      t,
    ],
  );

  return { handleEdit, handleDelete, handleSubmit, isSubmitting };
};

export default useDocumentActionHandlers;
